import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Components/NavBar";
import "../css/LandingPage.css";

function titleCaseName(name = "") {
  const s = String(name || "").trim();
  if (!s) return "Anonymous";
  return s
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function Stars({ value = 0 }) {
  const v = typeof value === "number" ? value : 0;
  return (
    <span className="stars" aria-label={`${v} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} aria-hidden>
          {v >= s ? "★" : "☆"}
        </span>
      ))}
    </span>
  );
}

export default function Reviews() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    rating: null,
    count: null,
    mapsUrl: null,
    zillowUrl: "https://www.zillow.com/profile/broscoehome",
  });
  const [loading, setLoading] = useState(true);

  const [sourceFilter, setSourceFilter] = useState("All"); // All | Google | Zillow

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const r = await fetch("/api/reviews/all");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();

        const normalized = (data.reviews || [])
          .filter((x) => (x.quote || "").trim().length > 0)
          .map((rev, i) => ({
            id: rev.id || `rev-${i}`,
            author: titleCaseName(rev.name),
            source: rev.source || "Review",
            rating: typeof rev.rating === "number" ? rev.rating : null,
            text: rev.quote || "",
            when: formatDate(rev.publishTime),
            viewUrl: rev.viewUrl || data.googleMapsUri || "#",
          }));

        if (!alive) return;

        setMeta((m) => ({
          ...m,
          rating: data.rating ?? null,
          count: data.userRatingCount ?? null,
          mapsUrl: data.googleMapsUri ?? null,
        }));

        setItems(normalized);
      } catch (e) {
        console.error("Failed to load reviews:", e);
        if (!alive) return;
        setItems([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const reviewCounts = useMemo(() => {
    return items.reduce(
      (acc, r) => {
        if (r.source === "Google") acc.google += 1;
        if (r.source === "Zillow") acc.zillow += 1;
        acc.total += 1;
        return acc;
      },
      { google: 0, zillow: 0, total: 0 }
    );
  }, [items]);

  const filteredItems = useMemo(() => {
    if (sourceFilter === "All") return items;
    return items.filter((x) => x.source === sourceFilter);
  }, [items, sourceFilter]);

  return (
    <div className="interior-page">
      <Navbar variant="reviews" />

      {/* HERO */}
      <header className="reviews-hero">
        <div className="reviews-hero-bg" aria-hidden />

        <div className="interior-inner reviews-hero-inner">
          <div className="reviews-hero-card">
            <div className="reviews-hero-eyebrow">Client Love</div>

            <h1 className="reviews-hero-title">Reviews</h1>
            <p className="reviews-hero-subtitle">
              Real feedback from real clients — pulled from Google plus all testimonials added from Zillow.
            </p>

            <div className="reviews-hero-stats">
              <div className="stat">
                <div className="stat-label">Google Rating</div>
                <div className="stat-value">
                  {meta.rating != null ? (
                    <>
                      <span className="stat-number">{meta.rating.toFixed(1)}</span>
                      <Stars value={meta.rating} />
                    </>
                  ) : (
                    <span className="stat-number">—</span>
                  )}
                </div>
                <div className="stat-foot">
                  {meta.count != null ? `${meta.count} Google reviews` : " "}
                </div>
              </div>

              <div className="stat">
                <div className="stat-label">Showing</div>
                <div className="stat-value">
                  <span className="stat-number">{reviewCounts.total}</span>
                </div>
                <div className="stat-foot">
                  {reviewCounts.zillow > 0
                    ? `${reviewCounts.google} Google + ${reviewCounts.zillow} Zillow`
                    : `${reviewCounts.google} Google`}
                </div>
              </div>
            </div>

            <div className="reviews-hero-actions">
              {meta.mapsUrl && (
                <a className="btn primary" href={meta.mapsUrl} target="_blank" rel="noreferrer">
                  View on Google
                </a>
              )}
              {meta.zillowUrl && (
                <a className="btn secondary" href={meta.zillowUrl} target="_blank" rel="noreferrer">
                  View on Zillow
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* LIST */}
      <main className="interior-inner">
        <div className="reviews-toolbar">
          <div>
            <h2 className="reviews-section-title">What clients are saying</h2>
            <div className="reviews-section-subtitle">
              Tip: Filter by source if you’re looking for a specific platform.
            </div>
          </div>

          <div className="reviews-filters" role="group" aria-label="Review filters">
            {["All", "Google", "Zillow"].map((v) => (
              <button
                key={v}
                type="button"
                className={`filter-chip ${sourceFilter === v ? "active" : ""}`}
                onClick={() => setSourceFilter(v)}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", color: "var(--text-secondary)", padding: "2.5rem 0" }}>
            Loading reviews…
          </div>
        ) : filteredItems.length ? (
          <div className="reviews-grid">
            {filteredItems.map((r) => (
              <article key={r.id} className="ui-card review-card">
                <div className="review-top">
                  <div className="review-author">{r.author}</div>

                  <div className="review-right">
                    <span className={`source-pill ${r.source === "Google" ? "google" : "zillow"}`}>
                      {r.source}
                    </span>
                    {r.rating != null && (
                      <span className="review-rating">
                        {r.rating.toFixed(1)} <span aria-hidden>★</span>
                      </span>
                    )}
                  </div>
                </div>

                {r.when && <div className="review-date">{r.when}</div>}

                <p className="review-quote">“{r.text}”</p>

                {r.viewUrl && r.viewUrl !== "#" && (
                  <div className="review-footer">
                    <a className="btn small secondary" href={r.viewUrl} target="_blank" rel="noreferrer">
                      {r.source === "Google" && "View on Google →"}
                      {r.source === "Zillow" && "View on Zillow →"}
                      {!["Google", "Zillow"].includes(r.source) && "View review →"}
                    </a>
                  </div>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", color: "var(--text-secondary)", padding: "2.5rem 0" }}>
            No reviews found yet.
          </div>
        )}
      </main>
    </div>
  );
}
